import { useEffect, useState } from 'react';
import { X, Mail, Send, AlertTriangle, Clock, Info } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { buildDraft, NEXT_STEP_LABELS } from '../../lib/outreachTemplates';

interface StepRow {
  project_id: string | null;
  project_name: string | null;
  customer_address: string | null;
  next_step: string;
  days_idle: number;
  is_due: boolean;
  opted_out: boolean;
  /** The latest request's route; the expired-link draft reads differently by it. */
  consent_route?: 'email_request' | 'self_download' | 'customer_request' | null;
}

interface HistoryRow {
  id: string;
  project_id: string | null;
  next_step: string;
  subject: string;
  sent_at: string;
}

interface ContextPayload {
  profile: {
    email: string;
    full_name: string | null;
    email_optout_at: string | null;
    excluded_from_digest: boolean;
  } | null;
  steps: StepRow[];
  history: HistoryRow[];
}

interface AdminEmailModalProps {
  userId: string;
  userEmail: string;
  userName: string | null;
  onClose: () => void;
}

function firstNameOf(fullName: string | null): string {
  if (!fullName) return '';
  const first = fullName.trim().split(/\s+/)[0] ?? '';
  // Names are stored however the user typed them at signup, and at least one
  // is all lowercase. Only the leading character is touched, so JoAnn and
  // McCune survive intact.
  return first ? first.charAt(0).toUpperCase() + first.slice(1) : '';
}

export default function AdminEmailModal({ userId, userEmail, userName, onClose }: AdminEmailModalProps) {
  const [ctx, setCtx] = useState<ContextPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [warning, setWarning] = useState<string | null>(null);

  const [selected, setSelected] = useState<StepRow | null>(null);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  const getJwt = async () => {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token ?? '';
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const jwt = await getJwt();
        const resp = await fetch(`/.netlify/functions/admin-send-email?userId=${encodeURIComponent(userId)}`, {
          headers: { Authorization: `Bearer ${jwt}` },
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.error || 'Failed to load context');
        setCtx(data as ContextPayload);

        // Open on whatever is most overdue — that is almost always the one
        // worth writing about.
        const due = (data.steps as StepRow[]).filter(s => s.is_due);
        if (due.length) applyDraft(due[0], data.profile?.full_name ?? userName);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load context');
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyDraft = (row: StepRow, fullName: string | null) => {
    setSelected(row);
    const draft = buildDraft(row.next_step, {
      firstName: firstNameOf(fullName),
      jobLabel: row.project_name || row.customer_address || 'your project',
      daysIdle: row.days_idle,
      consentRoute: row.consent_route ?? null,
    });
    setSubject(draft.subject);
    setMessage(draft.body);
  };

  const send = async () => {
    setSending(true);
    setError(null);
    setWarning(null);
    try {
      const jwt = await getJwt();
      const resp = await fetch('/.netlify/functions/admin-send-email', {
        method: 'POST',
        headers: { Authorization: `Bearer ${jwt}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          subject,
          message,
          projectId: selected?.project_id ?? null,
          nextStep: selected?.next_step ?? 'manual',
        }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Failed to send');
      if (data.warning) setWarning(data.warning);
      setSent(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const optedOut = Boolean(ctx?.profile?.email_optout_at);
  const excludedFromDigest = Boolean(ctx?.profile?.excluded_from_digest);
  // Only a real opt-out blocks. Being kept out of the digest is a note, not a
  // wall — test accounts have to stay reachable to test sending with.
  const blocked = optedOut;
  const canSend = !blocked && !sending && !sent && subject.trim() !== '' && message.trim() !== '';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">

        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-purple-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Send Email</h2>
              <p className="text-sm text-gray-500">{userName ? `${userName} · ` : ''}{userEmail}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading…</div>
          ) : (
            <>
              {optedOut && (
                <div className="mb-4 bg-amber-50 border border-amber-200 rounded-md p-3 flex gap-2 text-sm text-amber-800">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>This user has opted out of outreach email. Sending is disabled.</span>
                </div>
              )}

              {excludedFromDigest && !optedOut && (
                <div className="mb-4 bg-gray-50 border border-gray-200 rounded-md p-3 flex gap-2 text-sm text-gray-600">
                  <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    Kept out of the daily digest, so it will never show up as someone to chase.
                    You can still email it — handy for testing a send.
                  </span>
                </div>
              )}

              {sent && (
                <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-3 text-sm text-green-800">
                  Sent. Replies come to your inbox.
                  {warning && <span className="block mt-1 text-amber-700">{warning}</span>}
                </div>
              )}

              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">{error}</div>
              )}

              {/* What they're blocked on */}
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                What they are blocked on
              </p>
              {ctx?.steps.length ? (
                <div className="space-y-1.5 mb-5">
                  {ctx.steps.filter(s => s.next_step !== 'none').map((s, i) => (
                    <button
                      key={s.project_id ?? `none-${i}`}
                      onClick={() => applyDraft(s, ctx.profile?.full_name ?? userName)}
                      className={`w-full text-left px-3 py-2 rounded-md border text-sm transition-colors ${
                        selected?.project_id === s.project_id && selected?.next_step === s.next_step
                          ? 'border-purple-300 bg-purple-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-medium text-gray-900 truncate">
                          {s.project_name || s.customer_address || 'No project yet'}
                        </span>
                        <span className={`text-xs whitespace-nowrap ${s.is_due ? 'text-amber-700' : 'text-gray-400'}`}>
                          idle {s.days_idle}d
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {NEXT_STEP_LABELS[s.next_step] ?? s.next_step}
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 mb-5">Nothing outstanding.</p>
              )}

              {/* Compose */}
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
                Subject
              </label>
              <input
                value={subject}
                onChange={e => setSubject(e.target.value)}
                disabled={blocked || sent}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm mb-4 disabled:bg-gray-50"
              />

              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
                Message
              </label>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                disabled={blocked || sent}
                rows={14}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono leading-relaxed disabled:bg-gray-50"
              />
              <p className="text-xs text-gray-400 mt-1">
                Sent as plain text with your address as reply-to. A signature and an opt-out line are appended automatically.
              </p>

              {/* History */}
              {ctx?.history.length ? (
                <div className="mt-6">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                    Already sent
                  </p>
                  <ul className="space-y-1">
                    {ctx.history.map(h => (
                      <li key={h.id} className="flex items-start gap-2 text-xs text-gray-500">
                        <Clock className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        <span>
                          {new Date(h.sent_at).toLocaleDateString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric',
                          })}
                          {' — '}
                          <span className="text-gray-700">{h.subject}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50"
          >
            {sent ? 'Close' : 'Cancel'}
          </button>
          <button
            onClick={send}
            disabled={!canSend}
            className="flex items-center gap-2 px-4 py-2 text-sm rounded-md bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" />
            {sending ? 'Sending…' : 'Send Email'}
          </button>
        </div>

      </div>
    </div>
  );
}
